import { Fragment, useEffect, useMemo, useState, type ReactNode } from "react"
import { useRouter, type NextRouter } from "next/router"

import { useWorkspacePostHogGroup } from "@/feature-flags"
import {
  transport,
  TransportRequestError,
  TransportWorkspaceScopeProvider,
  useTransportQuery,
} from "@/transport"
import { useAuth, useOrganization } from "@clerk/nextjs"
import { LoadingScreen } from "@coval/ui"

import { logger } from "@/lib/logger"
import {
  buildOrganizationHomePath,
  buildWorkspacePath,
  DEFAULT_WORKSPACE_ID,
  isOrganizationGlobalPath,
  parseLegacyOrganizationRoute,
  parseWorkspaceRoute,
} from "@/utils/workspaceRoutes"

import {
  EMPTY_WORKSPACE_CONTEXT_VALUE,
  WorkspaceProvider,
  type WorkspaceContextValue,
} from "../contexts/WorkspaceContext"
import {
  readLastWorkspaceId,
  writeLastWorkspaceId,
} from "../utils/lastWorkspaceStorage"
import {
  findActiveDefaultWorkspace,
  findSentinelTargetWorkspace,
  resolveWorkspaceRoute,
} from "../utils/workspaceRouteResolution"

interface WorkspaceProviderRouteProps {
  children: ReactNode
}

type LegacyOrganizationRoute = NonNullable<
  ReturnType<typeof parseLegacyOrganizationRoute>
>
type WorkspaceRoute = NonNullable<ReturnType<typeof parseWorkspaceRoute>>

const INVARIANT_MISSING_ACTIVE_DEFAULT =
  "WorkspaceProvider invariant: listWorkspaces response must include an active DEFAULT workspace"
const log = logger("WorkspaceProviderRoute")

function WorkspaceAccessErrorState({
  asPath,
  detail,
  onChooseOrganization,
  onRetry,
  organizationSlug,
  retryLabel = "Retry this page",
  title,
}: {
  asPath: string
  detail: string
  onChooseOrganization: () => void
  onRetry: () => void
  organizationSlug: string | null
  retryLabel?: string
  title: string
}) {
  return (
    <div className="flex min-h-screen items-center justify-center bg-surface-default px-6">
      <div className="w-full max-w-lg rounded-lg border border-coval-border bg-surface-0 p-6 text-center shadow-sm">
        <h1 className="text-lg font-semibold text-coval-text">{title}</h1>
        <p className="mt-2 text-sm text-coval-text-muted">
          {detail} You can retry this page or choose another organization.
        </p>
        <dl className="mt-4 rounded-md bg-surface-1 p-3 text-left text-xs text-coval-text-muted">
          <div className="flex gap-2">
            <dt className="shrink-0 font-medium text-coval-text">Org:</dt>
            <dd className="truncate">{organizationSlug ?? "Unknown"}</dd>
          </div>
          <div className="mt-1 flex gap-2">
            <dt className="shrink-0 font-medium text-coval-text">Route:</dt>
            <dd className="truncate">{asPath}</dd>
          </div>
        </dl>
        <div className="mt-5 flex flex-col justify-center gap-3 sm:flex-row">
          <button
            type="button"
            className="inline-flex h-9 items-center justify-center rounded-md border border-coval-border bg-surface-0 px-4 text-sm font-medium text-coval-text transition-colors hover:bg-surface-0-hover focus-visible:outline-none"
            onClick={onRetry}
          >
            {retryLabel}
          </button>
          <button
            type="button"
            className="inline-flex h-9 items-center justify-center rounded-md border border-coval-border bg-surface-0 px-4 text-sm font-medium text-coval-text transition-colors hover:bg-surface-0-hover focus-visible:outline-none"
            onClick={onChooseOrganization}
          >
            Choose another organization
          </button>
        </div>
      </div>
    </div>
  )
}

function isRecoverableWorkspaceAccessError(error: unknown): boolean {
  return (
    error instanceof TransportRequestError &&
    (error.status === 401 || error.status === 403)
  )
}

export function WorkspaceProviderRoute({
  children,
}: WorkspaceProviderRouteProps) {
  const router = useRouter()
  const workspaceRoute = useMemo(
    () => parseWorkspaceRoute(router.asPath),
    [router.asPath],
  )
  const legacyRoute = useMemo(
    () => parseLegacyOrganizationRoute(router.asPath),
    [router.asPath],
  )
  const organizationRoute = workspaceRoute ?? legacyRoute

  if (!organizationRoute) {
    return (
      <GlobalWorkspaceProviderRoute>{children}</GlobalWorkspaceProviderRoute>
    )
  }

  return (
    <OrganizationWorkspaceProviderRoute
      legacyOrWorkspaceRoute={organizationRoute}
      router={router}
      workspaceRoute={workspaceRoute}
    >
      {children}
    </OrganizationWorkspaceProviderRoute>
  )
}

function GlobalWorkspaceProviderRoute({
  children,
}: WorkspaceProviderRouteProps) {
  const { organization } = useOrganization()
  useWorkspacePostHogGroup(organization?.id ?? null, null)

  return (
    <WorkspaceProvider value={EMPTY_WORKSPACE_CONTEXT_VALUE}>
      {children}
    </WorkspaceProvider>
  )
}

function OrganizationWorkspaceProviderRoute({
  children,
  legacyOrWorkspaceRoute,
  router,
  workspaceRoute,
}: WorkspaceProviderRouteProps & {
  legacyOrWorkspaceRoute: LegacyOrganizationRoute | WorkspaceRoute
  router: NextRouter
  workspaceRoute: WorkspaceRoute | null
}) {
  const [lastActiveContextValue, setLastActiveContextValue] =
    useState<WorkspaceContextValue | null>(null)
  const {
    organization: activeOrganization,
    isLoaded: isActiveOrganizationLoaded,
  } = useOrganization()
  const activeOrganizationSlug = activeOrganization?.slug ?? null
  const isRouteOrganizationConfirmed =
    legacyOrWorkspaceRoute.organizationSlug.toLowerCase() ===
    activeOrganizationSlug?.toLowerCase()
  const isRouteOrganizationActive =
    !isActiveOrganizationLoaded ||
    activeOrganizationSlug === null ||
    isRouteOrganizationConfirmed
  const organizationId = activeOrganization?.id ?? null
  const { isLoaded: isAuthLoaded, userId } = useAuth()
  const shouldFetchWorkspaces = isRouteOrganizationActive
  const isOrganizationGlobalRoute = isOrganizationGlobalPath(
    legacyOrWorkspaceRoute.relativePath,
  )
  // Workspace management and GitHub repository binding require the catalog.
  // Other organization settings only use it for optional workspace navigation.
  const requiresWorkspaceCatalog =
    !isOrganizationGlobalRoute ||
    legacyOrWorkspaceRoute.relativePath === "/settings/workspaces" ||
    legacyOrWorkspaceRoute.relativePath.startsWith("/settings/workspaces/") ||
    legacyOrWorkspaceRoute.relativePath === "/settings/integrations/github"
  const isDefaultSentinelRoute =
    !isOrganizationGlobalRoute &&
    workspaceRoute !== null &&
    workspaceRoute.workspaceId === DEFAULT_WORKSPACE_ID &&
    isRouteOrganizationActive
  // The organization home is sticky: a sentinel home URL opens the last workspace the user visited in this organization. Sentinel links to other pages keep the default workspace, since they usually point at a resource that lives there.
  const isHomeSentinelRoute =
    isDefaultSentinelRoute && workspaceRoute.relativePath === "/home"
  const isStickyHomeRoute = isHomeSentinelRoute && isRouteOrganizationConfirmed
  const workspacesQuery = useTransportQuery({
    operation: transport.operations.listWorkspaces,
    options: {
      enabled: shouldFetchWorkspaces,
      // The sticky choice must see current workspace statuses: a cached list can still show a workspace as active after another tab archived it.
      ...(isStickyHomeRoute ? { staleTime: 0 } : {}),
      reportErrors: shouldFetchWorkspaces && requiresWorkspaceCatalog,
    },
  })
  const workspaceCatalogLoading =
    shouldFetchWorkspaces && workspacesQuery.isLoading
  const resolved = useMemo(
    () =>
      resolveWorkspaceRoute({
        asPath: router.asPath,
        organizationRoute: isRouteOrganizationActive
          ? legacyOrWorkspaceRoute
          : null,
        workspaceCatalogLoading,
        workspaceResources: workspacesQuery.data?.workspaces ?? [],
        workspaceRoute,
      }),
    [
      isRouteOrganizationActive,
      legacyOrWorkspaceRoute,
      router.asPath,
      workspaceCatalogLoading,
      workspaceRoute,
      workspacesQuery.data,
    ],
  )
  const missingActiveDefaultWorkspace =
    shouldFetchWorkspaces &&
    Boolean(workspacesQuery.data) &&
    !resolved.hasActiveDefaultWorkspace
  const recoverableWorkspaceAccessError =
    shouldFetchWorkspaces &&
    isRecoverableWorkspaceAccessError(workspacesQuery.error)
  const requestedWorkspaceUnavailable =
    shouldFetchWorkspaces &&
    Boolean(workspacesQuery.data) &&
    workspaceRoute !== null &&
    !resolved.requestedWorkspaceAvailable
  // Replace the transient default sentinel with the active default workspace ID.
  const sentinelRedirectWorkspaceId = isDefaultSentinelRoute
    ? (findActiveDefaultWorkspace(workspacesQuery.data?.workspaces ?? [])?.id ??
      null)
    : null
  const settledWorkspaceId =
    shouldFetchWorkspaces &&
    isRouteOrganizationConfirmed &&
    workspaceRoute !== null &&
    !isDefaultSentinelRoute &&
    resolved.requestedWorkspaceAvailable
      ? (resolved.contextValue.currentWorkspace?.id ?? null)
      : null
  const selectedWorkspaceId =
    isRouteOrganizationConfirmed && shouldFetchWorkspaces
      ? (resolved.contextValue.currentWorkspace?.id ?? null)
      : null
  useWorkspacePostHogGroup(organizationId, selectedWorkspaceId)

  useEffect(() => {
    if (sentinelRedirectWorkspaceId === null || workspaceRoute === null) {
      return
    }
    // The stored workspace is keyed by organization, so the home settle waits until Clerk's active organization is the route organization. `RouteOrganizationGate` activates it for any member.
    if (isHomeSentinelRoute && !isRouteOrganizationConfirmed) {
      return
    }
    if (isStickyHomeRoute && (!isAuthLoaded || workspacesQuery.isFetching)) {
      return
    }
    // A failed refresh leaves the cached list in place, which may still show the stored workspace as active, so only a successful refresh can pick it.
    const targetWorkspaceId =
      isStickyHomeRoute && !workspacesQuery.error
        ? (findSentinelTargetWorkspace(
            workspacesQuery.data?.workspaces ?? [],
            readLastWorkspaceId({ organizationId, userId }),
          )?.id ?? sentinelRedirectWorkspaceId)
        : sentinelRedirectWorkspaceId
    const url = new URL(router.asPath, "https://coval.local")
    url.pathname = buildWorkspacePath(
      legacyOrWorkspaceRoute.organizationSlug,
      targetWorkspaceId,
      workspaceRoute.relativePath,
    )
    void router.replace(url.pathname + url.search + url.hash)
  }, [
    isAuthLoaded,
    isHomeSentinelRoute,
    isRouteOrganizationConfirmed,
    isStickyHomeRoute,
    legacyOrWorkspaceRoute,
    organizationId,
    router,
    router.asPath,
    sentinelRedirectWorkspaceId,
    userId,
    workspaceRoute,
    workspacesQuery.data,
    workspacesQuery.error,
    workspacesQuery.isFetching,
  ])

  useEffect(() => {
    if (settledWorkspaceId !== null) {
      writeLastWorkspaceId({ organizationId, userId }, settledWorkspaceId)
    }
  }, [organizationId, settledWorkspaceId, userId])

  if (isRouteOrganizationActive && requiresWorkspaceCatalog) {
    if (recoverableWorkspaceAccessError) {
      return (
        <WorkspaceAccessErrorState
          asPath={router.asPath}
          detail="The workspace request was rejected for this organization. This can happen if your current session is not authorized for this organization."
          organizationSlug={legacyOrWorkspaceRoute.organizationSlug}
          title="We could not access this organization workspace"
          onChooseOrganization={() => {
            void router.push("/select-org")
          }}
          onRetry={() => {
            router.reload()
          }}
        />
      )
    }

    if (shouldFetchWorkspaces && workspacesQuery.error) {
      throw workspacesQuery.error
    }

    if (shouldFetchWorkspaces && !workspacesQuery.data) {
      return <LoadingScreen />
    }

    if (shouldFetchWorkspaces && sentinelRedirectWorkspaceId !== null) {
      return <LoadingScreen />
    }

    if (missingActiveDefaultWorkspace) {
      log.warn(INVARIANT_MISSING_ACTIVE_DEFAULT, {
        asPath: router.asPath,
        organizationId,
        organizationSlug: legacyOrWorkspaceRoute.organizationSlug,
        workspaceSummary: workspacesQuery.data?.workspaces.map((workspace) => ({
          id: workspace.id,
          status: workspace.status,
          workspace_type: workspace.workspace_type,
        })),
      })
      throw new Error(INVARIANT_MISSING_ACTIVE_DEFAULT)
    }

    if (requestedWorkspaceUnavailable) {
      return (
        <WorkspaceAccessErrorState
          asPath={router.asPath}
          detail="The linked workspace is missing, archived, deleted, or belongs to another organization."
          organizationSlug={legacyOrWorkspaceRoute.organizationSlug}
          retryLabel="Go to Default workspace"
          title="This workspace is unavailable"
          onChooseOrganization={() => {
            void router.push("/select-org")
          }}
          onRetry={() => {
            const defaultWorkspaceId =
              resolved.contextValue.currentWorkspace?.id
            void router.push(
              defaultWorkspaceId
                ? buildWorkspacePath(
                    legacyOrWorkspaceRoute.organizationSlug,
                    defaultWorkspaceId,
                  )
                : buildOrganizationHomePath(
                    legacyOrWorkspaceRoute.organizationSlug,
                  ),
            )
          }}
        />
      )
    }
  }

  if (
    isRouteOrganizationActive &&
    lastActiveContextValue !== resolved.contextValue
  ) {
    setLastActiveContextValue(resolved.contextValue)
  }
  const contextValue =
    isRouteOrganizationActive || isOrganizationGlobalRoute
      ? resolved.contextValue
      : (lastActiveContextValue ?? resolved.contextValue)

  return (
    <TransportWorkspaceScopeProvider
      workspaceId={contextValue.currentWorkspace?.id ?? null}
    >
      <WorkspaceProvider value={contextValue}>
        <Fragment key={contextValue.currentWorkspace?.id ?? "global"}>
          {children}
        </Fragment>
      </WorkspaceProvider>
    </TransportWorkspaceScopeProvider>
  )
}
